import { useState, useRef, useEffect, useContext, useMemo } from "react";
import { Modal, Box, TextField, Fab, Tooltip } from "@material-ui/core";
import { useStyles } from "../../Styles/styles";
import PropTypes from "prop-types";
import { UserContext } from "../../Contexts/UserContext";
import { useMutation } from "@apollo/client";
import deleteButton from "../../Assets/deleteButton.png";
import addButton from "../../Assets/addButton.png";
import { REMOVE_ALIAS } from "./RemoveAlias";
import { IncidentsContext } from "../../Contexts/IncidentsContext";
import { PERSON_ACTIONS } from "../../Actions/PersonActions";
import { INCIDENT_ACTIONS } from "../../Actions/IncidentActions";
import { formatNameFromPersonObj, handleUuid } from "../../utils/utils";
import { ADD_OR_UPDATE_ALIAS } from "./AddOrUpdateAlias";
import { KeyboardDatePicker } from "@material-ui/pickers";
import moment from "moment";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { ModalHeader } from "../ModalHeader";
import PersonAutoSuggestRefactor from "../PersonAutoSuggestRefactor";

const Aliases = ({
  modalOpen,
  setModalOpen,
  owner,
  aliases = [],
  pisId,
  incidentId,
  inIncident,
  handlePersonOpening,
  setPausePolling = () => {},
}) => {
  const classes = useStyles();
  const { user } = useContext(UserContext);
  const { incidentDispatch, personDispatch } = useContext(IncidentsContext);
  const { UPDATE_PERSON_IN_PIS } = INCIDENT_ACTIONS;
  const { UPDATE_PERSON_ATTRIBUTE } = PERSON_ACTIONS;
  const tabWrapperRef = useRef(null);
  const ownerName = useMemo(() => formatNameFromPersonObj(owner), [owner]);

  useEffect(() => {
    if (!aliases || aliases.length === 0) {
      addBlankAliasHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aliases]);

  const [removeAliasMutation] = useMutation(REMOVE_ALIAS, {});
  const [addOrUpdateAliasMutation] = useMutation(ADD_OR_UPDATE_ALIAS, {});

  const removeAliasHandler = aliasId => {
    if (window.confirm("Are you sure you want to remove this alias?")) {
      if (!aliasId) {
        const filteredAliases = aliases.filter(alias => alias.id !== aliasId);
        if (!inIncident) {
          personDispatch({
            type: UPDATE_PERSON_ATTRIBUTE,
            payload: {
              personId: owner?.id,
              value: filteredAliases,
              property: "aliases",
            },
          });
        } else {
          incidentDispatch({
            type: UPDATE_PERSON_IN_PIS,
            payload: {
              incidentId: incidentId,
              pisId: pisId,
              personId: owner?.id,
              property: "aliases",
              value: filteredAliases,
            },
          });
        }
        return;
      } else {
        removeAliasMutation({
          variables: { input: { originatorId: user.id, aliasId } },
        }).then(response => {
          if (response.data.removeAlias.success) {
            const filteredAliases = aliases.filter(
              alias => alias.id !== aliasId,
            );
            if (!inIncident) {
              personDispatch({
                type: UPDATE_PERSON_ATTRIBUTE,
                payload: {
                  personId: owner?.id,
                  value: filteredAliases,
                  property: "aliases",
                },
              });
            } else {
              incidentDispatch({
                type: UPDATE_PERSON_IN_PIS,
                payload: {
                  incidentId: incidentId,
                  pisId: pisId,
                  personId: owner?.id,
                  property: "aliases",
                  value: filteredAliases,
                },
              });
            }
          } else {
            console.error(
              "Failed to remove alias:",
              response.data.removeAlias.errors,
            );
          }
        });
      }
    } else {
      return;
    }
  };

  const addBlankAliasHandler = () => {
    const EMPTY_ALIAS = {
      id: handleUuid(),
      description: "",
      target: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const newAliases = [...aliases, EMPTY_ALIAS];
    if (inIncident) {
      incidentDispatch({
        type: UPDATE_PERSON_IN_PIS,
        payload: {
          incidentId: incidentId,
          pisId: pisId,
          personId: owner?.id,
          property: "aliases",
          value: newAliases,
        },
      });
    } else {
      personDispatch({
        type: UPDATE_PERSON_ATTRIBUTE,
        payload: {
          personId: owner?.id,
          property: "aliases",
          value: newAliases,
        },
      });
    }
  };

  const updateAliasHandler = (aliasId, property, value) => {
    addOrUpdateAliasMutation({
      variables: {
        input: {
          originatorId: user.id,
          aliasId,
          ownerId: owner?.id,
          [property]: value,
        },
      },
    }).then(response => {
      if (property === "description") return;
      if (response.data.addOrUpdateAlias.errors.length === 0) {
        let foundMatch = false;
        const returnedAlias = response.data.addOrUpdateAlias.aliasRecord;
        const updatedAliases = aliases.map(alias => {
          if (
            alias.id === aliasId ||
            returnedAlias.id === alias.id ||
            returnedAlias.aliasUuid === alias.id
          ) {
            foundMatch = true;
            return { ...returnedAlias };
          }
          return alias;
        });

        if (!foundMatch) {
          const nullAliasIndex = aliases.findIndex(alias => alias.id === null);
          if (nullAliasIndex !== -1) {
            updatedAliases[nullAliasIndex] = {
              ...aliases[nullAliasIndex],
              [property]: value,
            };
          }
        }
        if (!inIncident) {
          personDispatch({
            type: UPDATE_PERSON_ATTRIBUTE,
            payload: {
              personId: owner?.id,
              property: "aliases",
              value: updatedAliases,
            },
          });
        } else {
          incidentDispatch({
            type: UPDATE_PERSON_IN_PIS,
            payload: {
              incidentId: incidentId,
              pisId: pisId,
              personId: owner?.id,
              property: "aliases",
              value: updatedAliases,
            },
          });
        }
      } else {
        console.error(
          "Failed to update alias:",
          response.data.addOrUpdateAlias.errors,
        );
      }
    });
  };

  const closeModal = () => {
    setModalOpen(false);
    // Reset aliases or perform any cleanup if necessary
  };

  return (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      className={classes.modal}
      data-cy="address-history-modal"
      keepMounted={false}
    >
      <div
        id={"tabs-wrapper"}
        style={{
          height: "75vh",
          maxWidth: "90vw",
          width: "auto",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
        className={classes.tabRoot}
        ref={tabWrapperRef}
      >
        <ModalHeader
          headerName={"Aliases for " + ownerName}
          nightMode={user?.nightMode}
          helpIconObjectName={"aliases"}
          setModalOpen={setModalOpen}
          centered={false}
        />
        {aliases &&
          aliases.length > 0 &&
          aliases.map((alias, index) => {
            const isLastAlias = index === aliases.length - 1;
            return (
              <Alias
                key={"alias-" + alias.id}
                alias={alias}
                index={index}
                removeAliasHandler={removeAliasHandler}
                updateAliasHandler={updateAliasHandler}
                addBlankAliasHandler={addBlankAliasHandler}
                isLastAlias={isLastAlias}
                handlePersonOpening={handlePersonOpening}
                closeModal={closeModal}
                incidentId={incidentId}
                user={user}
                setPausePolling={setPausePolling}
              />
            );
          })}
      </div>
    </Modal>
  );
};
export default Aliases;

const Alias = ({
  alias,
  removeAliasHandler,
  updateAliasHandler,
  addBlankAliasHandler,
  isLastAlias,
  handlePersonOpening,
  incidentId,
  user,
  setPausePolling = () => {},
}) => {
  const classes = useStyles();
  const [description, setDescription] = useState(alias.description || "");

  return (
    <Box
      key={alias.id}
      style={{
        display: "grid",
        gridTemplateColumns: "2fr 4fr auto auto",
        alignItems: "center",
        gap: "16px",
        padding: "10px",
        borderBottom: "1px solid #ccc",
      }}
    >
      <TextField
        label="Description"
        value={description}
        onChange={e => setDescription(e.target.value)}
        onBlur={() => {
          if (description !== alias.description) {
            updateAliasHandler(alias.id, "description", description);
          }
        }}
        multiline
        maxRows={4}
        style={{ width: "100%" }}
        InputLabelProps={{ shrink: true }}
      />

      {/* Alias Person Info */}
      {alias?.target ? (
        <AliasPerson
          person={alias.target}
          handlePersonOpening={handlePersonOpening}
          aliasId={alias.target.id}
        />
      ) : (
        <PersonAutoSuggestRefactor
          // business
          // mergePersonAction
          // person={alias?.target || {}}
          userRoles={user?.roles || []}
          userId={user?.id}
          dispatch={() => alert("Edit person dispatch called")}
          action={"DispatchNameReducerGoesHere"}
          handleAddPerson={person =>
            updateAliasHandler(alias.id, "targetId", person?.id)
          }
          incidentId={incidentId}
          canEdit={true}
          setPausePolling={setPausePolling}
        />
      )}

      <div
        style={{
          display: "flex",
          gap: "8px",
          justifyContent: "flex-end",
          minWidth: "80px",
        }}
      >
        <button
          type="button"
          onClick={() => removeAliasHandler(alias.id)}
          style={{ background: "none", border: 0, padding: 0 }}
        >
          <img
            src={deleteButton}
            className={classes.pngButton}
            alt="Delete Alias"
          />
        </button>
        {isLastAlias && (
          <button
            type="button"
            onClick={() => {
              if (description || alias.target) {
                addBlankAliasHandler();
              } else {
                //do nothing if an already blank alias is present
                alert(
                  "Please fill in the description or select a person before adding a new alias.",
                );
                return;
              }
            }}
            style={{ background: "none", border: 0, padding: 0 }}
          >
            <img
              src={addButton}
              style={{
                cursor: !description && !alias.target ? "not-allowed" : "pointer",
              }}
              className={classes.pngButton}
              alt="Add Alias"
            />
          </button>
        )}
      </div>
    </Box>
  );
};
Alias.propTypes = {
  alias: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  removeAliasHandler: PropTypes.func.isRequired,
  updateAliasHandler: PropTypes.func.isRequired,
  isLastAlias: PropTypes.bool.isRequired,
};

const AliasPerson = ({ person, handlePersonOpening, aliasId }) => {
  const name = formatNameFromPersonObj(person);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 0.5fr 1fr 2fr",
        gap: "12px",
        alignItems: "center",
      }}
    >
      <TextField
        label="Name"
        value={name}
        InputLabelProps={{ shrink: true }}
        disabled
      />
      <Tooltip title="Opens the person in a new tab" placement="top">
        <Fab size="small" color="primary" aria-label="open">
          <OpenInNewIcon
            onClick={() => {
              if (aliasId) {
                handlePersonOpening(aliasId, false);
              }
            }}
          />
        </Fab>
      </Tooltip>
      <KeyboardDatePicker
        label="DOB"
        value={
          person?.personalAttribute?.dob
            ? moment
                .utc(person?.personalAttribute?.dob, "YYYY-MM-DD HH:mm:ss UTC")
                .local()
            : null
        }
        format="MM/DD/YYYY"
        disabled
        InputLabelProps={{ shrink: true }}
        style={{ width: "150px" }}
      />
      <TextField
        label="Address"
        value={person?.personalAttribute?.homeAddress?.name || ""}
        disabled
        InputLabelProps={{ shrink: true }}
      />
    </div>
  );
};
AliasPerson.propTypes = {
  person: PropTypes.object.isRequired,
};

Aliases.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  setModalOpen: PropTypes.func.isRequired,
  owner: PropTypes.object.isRequired,
  aliases: PropTypes.arrayOf(PropTypes.object).isRequired,
  pisId: PropTypes.string,
  incidentId: PropTypes.string,
  inIncident: PropTypes.bool.isRequired,
  handlePersonOpening: PropTypes.func.isRequired,
};
